import { useEffect, useMemo, useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import { useNavigate } from '@tanstack/react-router'
import {
  IconChevronDown,
  IconDots,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react'
import { useGetClasses } from '@/services/classService'
import { useGetTests } from '@/services/pracTestService'
import { PracticeTestType } from '@/types/practice-test'
import { useParticipantStore } from '@/stores/participantStore'
import { cn } from '@/lib/utils'
import {
  getCreatorLabel,
  getMarksPercentage,
  getMarksStyle,
} from '@/utils/style'
import { FormatFullDateMonthYearHourMinute } from '@/utils/time'
import { useDebounce } from '@/hooks/useDebounce'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import TablePaginate from '@/components/ui/table-paginate'
import { ExamStatuses } from '@/features/class/constants'
import { TestTypeItems } from '@/features/students/constants'
import DraftTable from './DraftTable'

const PracticeTestTable = ({
  moduleId,
  testType,
}: {
  moduleId: string
  testType: string
}) => {
  const navigate = useNavigate()
  const { updateExamTestData } = useParticipantStore()

  const [page, setPage] = useState(1)
  const [tab, setTab] = useState(ExamStatuses.published)
  const [search, setSearch] = useState('')
  const [type, setType] = useState('all')
  const [filterClass, setFilterClass] = useState(0)

  const debouncedQuery = useDebounce(search) // wait 500ms

  const { tests, meta, isLoadingTests } = useGetTests({
    moduleId,
    page,
    status: tab.toLocaleLowerCase(),
    isPracTest: 'true',
    search: debouncedQuery.trim(),
    creator_type: type,
    class_id: filterClass,
  })

  const { classes, isLoadingClasses } = useGetClasses({
    module_id: moduleId,
    page: 1,
    size: 100,
  })

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery])

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const filterTitle = useMemo(() => {
    return TestTypeItems.find((item) => item.value === type)?.label
  }, [type])

  const classFilterTitle = useMemo(() => {
    if (!classes?.length || !filterClass) {
      return 'All'
    }

    return classes.find((item) => item.class_id === filterClass)?.class_name
  }, [classes, filterClass])
  return (
    <Card className='right-0 left-0 z-100 py-1'>
      <CardContent className='p-0'>
        <div className={'space-y-4 rounded-lg bg-[#FFFFFF] p-4'}>
          <div className='flex items-center justify-between'>
            <h1 className='text-2xl font-[400] text-[#002652]'>
              Practice Tests
            </h1>
            <Button
              onClick={() => {
                navigate({
                  to: '/create-practice-test/$moduleId',
                  params: { moduleId },
                })
              }}
            >
              <IconPlus /> Create Test
            </Button>
          </div>
          <Flex align={'center'} justify={'between'} className='max-h-10'>
            <Flex className='rounded-[10px] bg-[#F6F7F9] p-1' gap={'1'}>
              {Object.values(ExamStatuses).map((item: string) => (
                <div
                  className={cn(
                    'cursor-pointer rounded-[6px] px-3 py-1.5 text-sm',
                    tab === item
                      ? 'bg-white text-[#002652] shadow-[0_1px_2px_#023A7C1A]'
                      : 'bg-transparent text-[#667085]'
                  )}
                  onClick={() => {
                    setTab(item)
                    setPage(1)
                    setType('all')
                  }}
                >
                  {item}
                </div>
              ))}
            </Flex>

            <Flex align={'center'} gap={'3'}>
              <div className='relative'>
                <IconSearch className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2' />
                <Input
                  placeholder={'Test Name'}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className='h-10 w-[150px] pl-9 lg:w-[250px]'
                />
              </div>

              {/* class filter */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  value={filterClass}
                  className='h-10 bg-transparent'
                  disabled={isLoadingClasses || tab === ExamStatuses.draft}
                >
                  <Button
                    disabled={isLoadingClasses || tab === ExamStatuses.draft}
                    className='inline-flex h-10 items-center gap-1 border border-gray-300 bg-white px-3 text-black lg:px-4'
                  >
                    <span>Class: {classFilterTitle}</span>
                    <IconChevronDown className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='max-h-[200px] overflow-auto'>
                  <DropdownMenuItem
                    onClick={() => {
                      setFilterClass(0)
                      setPage(1)
                    }}
                    className='cursor-pointer px-4 py-2 hover:bg-gray-100'
                  >
                    All
                  </DropdownMenuItem>
                  {classes?.map((item) => (
                    <DropdownMenuItem
                      onClick={() => {
                        setFilterClass(item.class_id)
                        setPage(1)
                      }}
                      key={item.class_id}
                      className='cursor-pointer px-4 py-2 hover:bg-gray-100'
                    >
                      {item.class_name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* type  */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  value={type}
                  className='h-10 bg-transparent'
                  disabled={tab === ExamStatuses.draft}
                >
                  <Button
                    disabled={tab === ExamStatuses.draft}
                    className='inline-flex h-10 items-center gap-1 border border-gray-300 bg-white px-3 text-black lg:px-4'
                  >
                    <span>Type: {filterTitle}</span>
                    <IconChevronDown className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {TestTypeItems.map((item) => (
                    <DropdownMenuItem
                      onClick={() => {
                        setType(item.value)
                        setPage(1)
                      }}
                      key={item.value}
                      className='cursor-pointer px-4 py-2 hover:bg-gray-100'
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </Flex>
          </Flex>
        </div>
        {tab !== ExamStatuses.draft && (
          <Table>
            <TableHeader>
              <TableRow className='bg-[#F2F4F7]'>
                <TableHead className='font-mundial p-4 text-sm font-[600] text-[#002652]'>
                  Name
                </TableHead>
                <TableHead className='font-mundial w-[140px] text-center text-sm font-[600] text-[#002652]'>
                  Participants
                </TableHead>
                <TableHead className='font-mundial w-[140px] text-center text-sm font-[600] text-[#002652]'>
                  Attempts
                </TableHead>
                <TableHead className='font-mundial w-[140px] text-center text-sm font-[600] text-[#002652]'>
                  Average AI Mark
                </TableHead>
                <TableHead className='font-mundial w-[140px] text-center text-sm font-[600] text-[#002652]'>
                  Average Tutor Mark
                </TableHead>
                <TableHead className='font-mundial w-[140px] text-sm font-[600] text-[#002652]'>
                  Type
                </TableHead>
                <TableHead className='font-mundial max-w-[20%] text-sm font-[600] text-[#002652]'>
                  Class
                </TableHead>
                <TableHead className='font-mundial max-w-[10%] text-center text-sm font-[600] text-[#002652]' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingTests ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Flex justify={'center'}>
                      <Spinner size={'3'} />
                    </Flex>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {tests?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className='h-24 text-center'>
                        No data found.
                      </TableCell>
                    </TableRow>
                  )}
                  {tests?.map((item) => (
                    <TableRow key={item.id} className='hover:bg-gray-50'>
                      <TableCell className='w-[230px] max-w-[230px] min-w-[230px] truncate p-4'>
                        <div className='max-w-[230px] min-w-[230px] truncate text-sm font-[400] text-[#002652]'>
                          {item.name}{' '}
                          <div className='truncate text-sm font-[300] text-[#475467]'>
                            Created date:{' '}
                            {FormatFullDateMonthYearHourMinute(item.created_at)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className='w-[15%] text-center text-sm font-[300] text-[#002652]'>
                        {item.participants ?? 'N/A'}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'w-[15%] text-center text-sm font-[300] text-[#002652]'
                        )}
                      >
                        {item.total_attempts ?? 'N/A'}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'w-[15%] text-center text-sm font-[300] text-[#002652]',
                          getMarksStyle(item.avg_ai_marks)
                        )}
                      >
                        {getMarksPercentage(item.avg_ai_marks)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'w-[15%] text-center text-sm font-[300] text-[#002652]',
                          getMarksStyle(item.avg_tutor_marks)
                        )}
                      >
                        {getMarksPercentage(item.avg_tutor_marks)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'w-[15%] text-sm font-[300] text-[#002652] capitalize'
                        )}
                      >
                        {getCreatorLabel(item.creator_type)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'max-w-[20%] truncate text-sm font-[300] text-[#002652]'
                        )}
                      >
                        <p className='max-w-[300px] truncate'>
                          {type === 'common'
                            ? 'All class'
                            : item.class_info?.map((c) => c.name).join(', ')}
                        </p>
                      </TableCell>
                      <TableCell className='w-[5%] text-center'>
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant='link'
                              className='h-auto p-0 font-medium text-[#0057B8] hover:text-blue-800'
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <IconDots />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align='end'
                            onCloseAutoFocus={(e) => e.preventDefault()}
                          >
                            {item.creator_type.toLocaleLowerCase() !==
                              PracticeTestType.STUDENT && (
                              <DropdownMenuItem
                                onClick={() => {
                                  navigate({
                                    to: '/create-practice-test/detail/$ptId/$moduleId',
                                    params: {
                                      ptId: item.id.toString(),
                                      moduleId,
                                    },
                                  })
                                }}
                              >
                                View details
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => {
                                updateExamTestData(item)
                                navigate({
                                  to: '/marking/list-participant/$ptId/$classId',
                                  params: {
                                    ptId: item.id.toString(),
                                    classId: 'all',
                                  },
                                  search: { source: 'TestTable' },
                                })
                              }}
                            >
                              {item.creator_type.toLocaleLowerCase() !==
                              PracticeTestType.STUDENT
                                ? 'View participants'
                                : 'View'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        )}

        {tab === ExamStatuses.draft && (
          <DraftTable
            testType={testType}
            moduleId={moduleId}
            tests={tests ?? []}
            isLoadingTests={isLoadingTests}
          />
        )}

        {meta && meta.total_pages > 1 && (
          <div className='flex justify-end p-4'>
            <TablePaginate
              itemPerPage={meta.items_per_page}
              totalItems={meta.total_items}
              page={page}
              totalPages={meta.total_pages}
              onPageChange={handlePageChange}
              readOnlyPageDisplay={false}
              hideOnSinglePage={false}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default PracticeTestTable
